import {
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  MoreHorizontal,
  Star,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Progress } from '../../components/ui/progress';
import { cn } from '../../lib/utils';
import { Asset, AssetStatus } from '../../types/asset';
import { getCategoryColorClass, getCategoryIcon } from '../../utils/categoryUtils';
import { NFTRecord } from '../../types/nft/nfts';
import ApproveButton from '../nft/approve.asset';
import CreateRentalButton from '../nft/create.rental';
import ResellPrice from '../nft/resellprice.resell';

const statusConfig: Record<AssetStatus, { label: string; className: string }> = {
  DRAFT: { label: 'Draft', className: 'bg-muted text-muted-foreground border-border' },
  UNDER_REVIEW: { label: 'Under Review', className: 'bg-info/20 text-info border-info/30' },
  ACTIVE: { label: 'Active', className: 'bg-primary/20 text-primary border-primary/30' },
  active: { label: 'Active', className: 'bg-primary/20 text-primary border-primary/30' },
  DISTRIBUTING: {
    label: 'Distributing',
    className: 'bg-success/20 text-success border-success/30',
  },
  MATURE: { label: 'Mature', className: 'bg-warning/20 text-warning border-warning/30' },
  EXITED: { label: 'Exited', className: 'bg-muted text-muted-foreground border-border' },
  PENDING: { label: 'Pending', className: 'bg-warning/20 text-warning border-warning/30' },
  APPROVED: { label: 'Approved', className: 'bg-success/20 text-success border-success/30' },
  REJECTED: {
    label: 'Rejected',
    className: 'bg-destructive/20 text-destructive border-destructive/30',
  },
  inactive: { label: 'Inactive', className: 'bg-muted text-muted-foreground border-border' },
};

interface AssetCardProps {
  asset: Asset;
  onEdit?: (asset: Asset) => void;
  onDelete?: (assetId: string) => void;
  onViewDetails?: (asset: Asset) => void;
  onViewPerformance?: (asset: Asset) => void;
  onFeaturedToggle?: (asset: Asset, isFeatured: boolean) => void;
}

export function AssetCard({
  asset,
  onEdit,
  onDelete,
  onViewDetails,
  onViewPerformance,
  onFeaturedToggle,
}: AssetCardProps) {
  const navigate = useNavigate();
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const categoryLabel = asset.categoryName || 'Uncategorized';
  const CategoryIcon = getCategoryIcon(categoryLabel);
  const categoryColorClass = getCategoryColorClass(categoryLabel);
  const status = statusConfig[asset.status] || statusConfig.ACTIVE;
  const tokensIssued = asset.tokensIssued || 0;
  const totalTokens = asset.totalTokens || asset.maxSupply || 0;
  const tokenProgress = totalTokens > 0 ? (tokensIssued / totalTokens) * 100 : 0;

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(asset);
    } else {
      navigate(`/assets/${asset.id || asset._id}`);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(asset);
    } else {
      navigate(`/assets/${asset.id || asset._id}/edit`);
    }
  };

  const handleDelete = () => {
    if (onDelete && (asset.id || asset._id)) {
      onDelete(asset.id || asset._id);
    }
  };

  // Get image URL - prefer original, fallback to compressed, then to a placeholder
  const imageUrl = asset.image?.original || asset.image?.compressed || null;
  const sideImages = asset.image?.sideImages || [];
  const totalImages = 1 + sideImages.length; // Main image + side images

  // Prepare all images for gallery (main image + side images)
  const allImages = [
    { url: imageUrl, description: asset.name || asset.assetName || 'Main Image' },
    ...sideImages.map((img, index) => ({
      url: img.url || '',
      description: img.description || `Side Image ${index + 1}`,
    })),
  ].filter((img) => img.url); // Filter out empty URLs

  const handleImageClick = () => {
    if (allImages.length > 0) {
      setCurrentImageIndex(0);
      setIsImageGalleryOpen(true);
    }
  };

  const handleThumbnailClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allImages.length > 0) {
      setCurrentImageIndex(0);
      setIsImageGalleryOpen(true);
    }
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className='glass-card rounded-xl overflow-hidden transition-all duration-300 hover:border-primary/30 hover:shadow-glow-primary/10 group animate-slide-up'>
      {/* Property Image Section */}
      {imageUrl ? (
        <div
          className='relative aspect-[4/3] w-full overflow-hidden bg-muted cursor-pointer'
          onClick={handleImageClick}
        >
          <img
            src={imageUrl}
            alt={asset.name || asset.assetName || 'Asset image'}
            className='w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110'
            onError={(e) => {
              // Fallback to placeholder if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
          {/* Gradient Overlay */}
          <div className='absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity duration-500' />

          {/* Category Badge on Image */}
          <div className='absolute top-3 left-3'>
            <div
              className={cn(
                'flex items-center justify-center w-10 h-10 rounded-lg border-2 border-background/50 shadow-lg',
                categoryColorClass
              )}
            >
              <CategoryIcon className='w-5 h-5' />
            </div>
          </div>

          {/* Status Badge on Image */}
          <div className='absolute top-3 right-3 flex flex-col items-end gap-2'>
            {asset.isFeatured && (
              <span className='inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border shadow-lg backdrop-blur-sm bg-warning/20 text-warning border-warning/30'>
                <Star className='w-3 h-3 fill-current' />
                Featured
              </span>
            )}
            <span
              className={cn(
                'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border shadow-lg backdrop-blur-sm',
                status.className
              )}
            >
              {status.label}
            </span>
          </div>

          {/* Side Images Thumbnail Indicators */}
          {sideImages.length > 0 && (
            <div
              className='absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1.5 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 shadow-lg cursor-pointer hover:bg-background/90 transition-colors'
              onClick={handleThumbnailClick}
              title={`Click to view all ${totalImages} images`}
            >
              {/* Main image indicator */}
              <div className='w-2 h-2 rounded-full bg-primary border border-primary-foreground/20' />
              {/* Side images indicators */}
              {sideImages.slice(0, 4).map((_, index) => (
                <div
                  key={index}
                  className='w-2 h-2 rounded-full bg-muted-foreground/40 border border-border/50'
                />
              ))}
              {/* Show count if more than 4 side images */}
              {sideImages.length > 4 && (
                <span className='text-xs font-medium text-muted-foreground ml-0.5'>
                  +{sideImages.length - 4}
                </span>
              )}
              {/* Total count badge */}
              <span className='text-xs font-semibold ml-1 px-1.5 py-0.5 rounded bg-primary/20 text-primary border border-primary/30'>
                {totalImages}
              </span>
            </div>
          )}
        </div>
      ) : (
        // Placeholder when no image
        <div className='relative aspect-[4/3] w-full overflow-hidden bg-muted/50 flex items-center justify-center'>
          <div className='text-center'>
            <div
              className={cn(
                'flex items-center justify-center w-16 h-16 rounded-xl mx-auto mb-3',
                categoryColorClass
              )}
            >
              <CategoryIcon className='w-8 h-8' />
            </div>
            <p className='text-xs text-muted-foreground'>No image available</p>
          </div>
        </div>
      )}

      <div className='p-4 sm:p-6'>
        {/* Header with Icon, Title, and Status Badge */}
        <div className='flex items-start justify-between mb-4'>
          <div className='flex items-start gap-3 flex-1 min-w-0'>
            <div
              className={cn(
                'flex items-center justify-center w-10 h-10 rounded-lg transition-transform group-hover:scale-105 flex-shrink-0',
                categoryColorClass
              )}
            >
              <CategoryIcon className='w-5 h-5' />
            </div>
            <div className='flex-1 min-w-0'>
              <h3 className='font-semibold text-lg text-foreground group-hover:text-primary transition-colors truncate mb-1 border-b border-border/30 pb-1'>
                {asset.name || asset.assetName || '—'}
              </h3>
              {asset.location && (
                <p className='text-sm text-muted-foreground truncate mb-2'>{asset.location}</p>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0'
              >
                <MoreHorizontal className='w-4 h-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleViewDetails}>
                <Eye className='w-4 h-4 mr-2' />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleEdit}>
                <Edit className='w-4 h-4 mr-2' />
                Edit Asset
              </DropdownMenuItem>
              {onViewPerformance && (
                <DropdownMenuItem onClick={() => onViewPerformance(asset)}>
                  <TrendingUp className='w-4 h-4 mr-2' />
                  View Performance
                </DropdownMenuItem>
              )}
              {onFeaturedToggle && (
                <DropdownMenuItem
                  onClick={() => onFeaturedToggle(asset, !asset.isFeatured)}
                >
                  <Star className='w-4 h-4 mr-2' />
                  {asset.isFeatured ? 'Remove Featured' : 'Mark as Featured'}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className='text-destructive' onClick={handleDelete}>
                <Trash2 className='w-4 h-4 mr-2' />
                Delete Asset
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Metrics Grid */}
        <div className='grid grid-cols-2 gap-2 sm:gap-4 mb-4'>
          <div className='p-2 sm:p-3 rounded-lg bg-muted/30 border border-border/50'>
            <p className='text-xs text-muted-foreground mb-1'>Valuation</p>
            <p className='text-base sm:text-lg font-bold text-foreground'>
              {asset.valuation ? formatCurrency(asset.valuation) : '—'}
            </p>
          </div>
          <div className='p-2 sm:p-3 rounded-lg bg-muted/30 border border-border/50'>
            <p className='text-xs text-muted-foreground mb-1'>Yield Rate</p>
            <p className='text-base sm:text-lg font-bold text-success'>
              {asset.yieldRate || asset.annualYield
                ? `${asset.yieldRate || asset.annualYield}%`
                : '—'}
            </p>
          </div>
          <div className='p-2 sm:p-3 rounded-lg bg-muted/30 border border-border/50'>
            <p className='text-xs text-muted-foreground mb-1'>Token Symbol</p>
            {asset.tokenSymbol ? (
              <code className='text-xs sm:text-sm font-mono text-primary bg-primary/10 px-2 py-0.5 rounded'>
                ${asset.tokenSymbol}
              </code>
            ) : (
              <span className='text-base sm:text-lg font-bold text-muted-foreground'>—</span>
            )}
          </div>
          <div className='p-2 sm:p-3 rounded-lg bg-muted/30 border border-border/50'>
            <p className='text-xs text-muted-foreground mb-1'>Investors</p>
            <p className='text-base sm:text-lg font-bold text-foreground'>
              {asset.investorCount != null ? asset.investorCount.toLocaleString() : '—'}
            </p>
          </div>
        </div>

        {/* Token Distribution Progress */}
        {totalTokens > 0 && (
          <div className='p-3 rounded-lg bg-muted/20 border border-border/30 mb-4'>
            <div className='flex items-center justify-between text-xs mb-2'>
              <span className='text-muted-foreground'>Token Distribution</span>
              <span className='text-foreground font-medium'>
                {tokensIssued.toLocaleString()} / {totalTokens.toLocaleString()}
              </span>
            </div>
            <Progress value={tokenProgress} className='h-2' />
            <p className='text-xs text-muted-foreground mt-1 text-right'>
              {tokenProgress.toFixed(1)}% distributed
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className='flex flex-col gap-2 mt-4'>
          <div className='grid grid-cols-2 gap-2 w-full'>
            <div className='w-full [&_button]:w-full'>
              <ApproveButton record={asset as unknown as NFTRecord} />
            </div>
            <div className='w-full [&_button]:w-full'>
              <CreateRentalButton record={asset as unknown as NFTRecord} />
            </div>
          </div>
          <div className='w-full [&_button]:w-full'>
            <ResellPrice record={asset as unknown as NFTRecord} />
          </div>
        </div>
      </div>

      {/* Image Gallery Modal */}
      <Dialog open={isImageGalleryOpen} onOpenChange={setIsImageGalleryOpen}>
        <DialogContent className='max-w-[95vw] sm:max-w-2xl lg:max-w-4xl w-full p-0 gap-0 max-h-[95vh] overflow-hidden flex flex-col'>
          <DialogHeader className='px-3 sm:px-4 lg:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 flex-shrink-0'>
            <DialogTitle className='text-base sm:text-lg lg:text-xl pr-8'>
              <span className='truncate block'>{asset.name || asset.assetName || 'Asset'}</span>
              <span className='text-xs sm:text-sm text-muted-foreground font-normal'>
                Images ({currentImageIndex + 1} of {allImages.length})
              </span>
            </DialogTitle>
          </DialogHeader>

          {allImages.length > 0 && (
            <div className='relative flex-1 min-h-0 flex flex-col'>
              {/* Main Image Display */}
              <div className='relative w-full bg-muted flex items-center justify-center flex-1 min-h-0 overflow-hidden'>
                <img
                  src={allImages[currentImageIndex].url}
                  alt={allImages[currentImageIndex].description}
                  className='max-w-full max-h-[50vh] sm:max-h-[60vh] lg:max-h-[70vh] object-contain'
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />

                {/* Navigation Arrows */}
                {allImages.length > 1 && (
                  <>
                    <Button
                      variant='outline'
                      size='icon'
                      className='absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-background/80 backdrop-blur-sm border-2 hover:bg-background z-10'
                      onClick={handlePreviousImage}
                      aria-label='Previous image'
                    >
                      <ChevronLeft className='h-4 w-4 sm:h-5 sm:w-5' />
                    </Button>
                    <Button
                      variant='outline'
                      size='icon'
                      className='absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-background/80 backdrop-blur-sm border-2 hover:bg-background z-10'
                      onClick={handleNextImage}
                      aria-label='Next image'
                    >
                      <ChevronRight className='h-4 w-4 sm:h-5 sm:w-5' />
                    </Button>
                  </>
                )}

                {/* Image Description */}
                {allImages[currentImageIndex].description && (
                  <div className='absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 max-w-[90%] sm:max-w-none'>
                    <p className='text-xs sm:text-sm font-medium text-foreground truncate'>
                      {allImages[currentImageIndex].description}
                    </p>
                  </div>
                )}
              </div>

              {/* Thumbnail Strip */}
              {allImages.length > 1 && (
                <div className='px-3 sm:px-4 lg:px-6 pb-3 sm:pb-4 lg:pb-6 pt-3 sm:pt-4 border-t flex-shrink-0'>
                  <div className='flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'>
                    {allImages.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={cn(
                          'relative flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all',
                          'w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20',
                          currentImageIndex === index
                            ? 'border-primary ring-2 ring-primary/20'
                            : 'border-border hover:border-primary/50'
                        )}
                        aria-label={`View image ${index + 1}: ${img.description}`}
                      >
                        <img
                          src={img.url}
                          alt={img.description}
                          className='w-full h-full object-cover'
                        />
                        {currentImageIndex === index && (
                          <div className='absolute inset-0 bg-primary/20' />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
